/**
 * What the practice changed: the same questions, two readings.
 *
 * A practice ends with a number, and a number on its own is not an answer. The
 * question a learner actually has is *did that help?* — which can only be answered
 * against the first attempt, so this screen is built as a comparison and never as a
 * score: the first reading (as the report measured it) beside the second, the
 * questions that moved, and the ones that did not.
 *
 * Three decisions shape it:
 *
 *  - **The first score is shown as it was.** It is the report's own number, not
 *    recomputed from anything here, because a baseline that moves with the second
 *    attempt cannot be compared with it.
 *  - **A question nobody was asked is not a failure.** A practice asks only what
 *    the report named, and a learner who closed the book on question six did not
 *    get the seventh wrong — it was never put to them, and saying otherwise would
 *    invent a mistake.
 *  - **It offers the next step it names.** The questions still open are handed back
 *    as another practice, because "these three are still open" and nothing to do
 *    about it is the verdict this whole feature exists to replace.
 *
 * Like the report, it is printed on the book's own paper in the book's own type:
 * it is the book's ending, not an application screen that appears afterwards.
 */
import {
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native'
import {Feather} from '@expo/vector-icons'
import {Plural, Trans, useLingui} from '@lingui/react/macro'

import {
  directionStyle,
  directionTextStyle,
  type FlashBookDirection,
} from '#/lib/flashbookDirection'
import {
  type PracticeComparison,
  type PracticeQuestion,
  type PracticeVerdict,
} from '#/lib/flashbookReRead'
import {
  FlashBookPlayThemeProvider,
  useFlashBookPlayDesign,
  useFlashBookPlayPalette,
  usePlayPrimaryAction,
  usePlayStackGap,
} from '#/components/flashbooks/FlashBookPlayTheme'
import {
  useCopyStyle,
  useReportType,
} from '#/components/flashbooks/useReportChrome'
import {type FlashBookDesign} from '#/themes-flashbook/design'
import {type FlashBookThemeTokens} from '#/types/flashbook'

type Props = {
  bookTitle: string
  comparison: PracticeComparison
  themeTokens: FlashBookThemeTokens
  design?: FlashBookDesign
  direction?: FlashBookDirection
  /** Sit the questions that are still open, again. */
  onPractiseAgain?: () => void
  /**
   * Where the way back leads.
   *
   * A practice of a read that *ended* belongs to the report — that report is the
   * read's own ending and the number this screen compares against. A practice of a
   * read still going belongs to the cover, because a part-way read has no report
   * to go back to. The screen is the same screen either way; only the destination
   * and the word for it change.
   */
  backTo?: 'report' | 'cover'
  /** Back to the read's own ending, wherever that is. */
  onBack?: () => void
  onDone: () => void
}

export function FlashBookPracticeResult({
  themeTokens,
  design,
  direction,
  backTo = 'report',
  ...props
}: Props) {
  return (
    <FlashBookPlayThemeProvider
      tokens={themeTokens}
      design={design}
      direction={direction}>
      <PracticeBody
        {...props}
        backTo={backTo}
        tok={themeTokens}
        direction={direction}
      />
    </FlashBookPlayThemeProvider>
  )
}

function PracticeBody({
  bookTitle,
  comparison,
  tok,
  direction,
  onPractiseAgain,
  onBack,
  onDone,
  backTo,
}: Omit<Props, 'themeTokens' | 'design' | 'backTo'> & {
  tok: FlashBookThemeTokens
  /** Resolved by the wrapper, so the body never has to guess a default. */
  backTo: 'report' | 'cover'
}) {
  const {t: l} = useLingui()
  const type = useReportType()
  const copy = useCopyStyle()
  const pal = useFlashBookPlayPalette()
  const pagePad = useFlashBookPlayDesign().page.padding
  const stackGap = usePlayStackGap(18)
  const action = usePlayPrimaryAction(48)
  const titleDir = directionTextStyle(direction ?? 'ltr')
  const rtl = direction === 'rtl'
  const mirrored = rtl ? directionStyle('rtl') : null

  const {before, after, fixed, remaining} = comparison
  const gained = after.pct - before.pct
  const allFixed = remaining.length === 0

  const headline = allFixed
    ? l`All fixed`
    : fixed.length === 0
      ? l`None fixed yet`
      : l`${fixed.length} fixed`

  return (
    <View
      testID="flashbook-practice-result"
      style={[styles.root, {backgroundColor: tok.pageBg}]}>
      <ScrollView
        style={styles.flex}
        contentContainerStyle={[
          styles.content,
          {paddingHorizontal: pagePad, gap: stackGap},
        ]}
        showsVerticalScrollIndicator={false}>
        {/* ── The headline ────────────────────────────────────────────────── */}
        <View style={[styles.head, mirrored]}>
          <Text style={[type.caption, copy.text, {color: pal.muted}, titleDir]}>
            {bookTitle}
          </Text>
          <Text
            testID="flashbook-practice-headline"
            style={[
              type.display,
              copy.text,
              {color: pal.text, textAlign: 'center'},
            ]}>
            {headline}
          </Text>
          <Text
            style={[
              type.body,
              copy.text,
              {color: pal.body, textAlign: 'center'},
            ]}>
            {allFixed ? (
              <Trans>
                Every question you came back for is right. Nothing left to redo.
              </Trans>
            ) : (
              <Plural
                value={remaining.length}
                one="One question is still open below."
                other="# questions are still open below."
              />
            )}
          </Text>
        </View>

        {/* ── The two scores ──────────────────────────────────────────────── */}
        <View
          testID="flashbook-practice-score"
          style={[
            styles.scoreCard,
            {borderColor: pal.paperHairline, backgroundColor: pal.surfaceAlt},
            mirrored,
          ]}>
          <ScoreSide
            testID="flashbook-practice-score--before"
            label={l`First try`}
            pct={before.pct}
            points={before.points}
            maxPoints={before.maxPoints}
            ink={pal.muted}
            bar={pal.muted}
          />
          <View style={styles.scoreArrow}>
            <Feather
              name={rtl ? 'arrow-left' : 'arrow-right'}
              size={18}
              color={pal.muted}
            />
          </View>
          <ScoreSide
            testID="flashbook-practice-score--after"
            label={l`Now`}
            pct={after.pct}
            points={after.points}
            maxPoints={after.maxPoints}
            ink={tok.accent}
            bar={tok.accent}
          />
        </View>

        <Text
          testID="flashbook-practice-delta"
          style={[
            type.label,
            copy.text,
            {color: pal.text, textAlign: 'center'},
          ]}>
          {gained > 0 ? (
            <Trans>
              {gained} points better on the same {before.maxPoints} points —{' '}
              {comparison.delta} more earned.
            </Trans>
          ) : gained === 0 ? (
            <Trans>Same score on the same questions.</Trans>
          ) : (
            <Trans>
              {Math.abs(gained)} points lower this time — worth another go.
            </Trans>
          )}
        </Text>

        {/* ── Each question, side by side ─────────────────────────────────── */}
        <View style={styles.section}>
          <Text
            style={[
              type.title,
              copy.text,
              {color: pal.text},
              styles.sectionTitle,
            ]}>
            <Trans>Question by question</Trans>
          </Text>
          {comparison.questions.map(question => (
            <QuestionRow
              key={question.blockId}
              question={question}
              positive={pal.positive}
              negative={pal.negative}
              warning={pal.warning}
              accent={tok.accent}
            />
          ))}
        </View>

        {/* ── What to do next ─────────────────────────────────────────────── */}
        <View style={[styles.actions, mirrored]}>
          {onPractiseAgain && !allFixed && (
            <TouchableOpacity
              testID="flashbook-practice-again"
              accessibilityRole="button"
              accessibilityHint={l`Asks the questions that are still open`}
              onPress={onPractiseAgain}
              style={[
                styles.actionBtn,
                {backgroundColor: tok.accent},
                action.style,
              ]}>
              <Feather
                name="repeat"
                size={15}
                color={action.ink(true) ?? pal.onAccent}
              />
              <Text
                style={[
                  type.label,
                  copy.text,
                  {color: action.ink(true) ?? pal.onAccent},
                  action.labelStyle(true),
                ]}>
                <Plural
                  value={remaining.length}
                  one="Practise the one still open"
                  other="Practise the # still open"
                />
              </Text>
            </TouchableOpacity>
          )}
          {onBack && (
            <TouchableOpacity
              testID="flashbook-practice-back"
              accessibilityRole="button"
              accessibilityHint={
                backTo === 'cover'
                  ? l`Returns to the book's cover`
                  : l`Returns to the read's own report`
              }
              onPress={onBack}
              style={[
                styles.actionBtn,
                {
                  borderColor: pal.paperHairline,
                  backgroundColor: pal.surfaceAlt,
                  borderRadius: 14,
                },
              ]}>
              <Feather
                name={backTo === 'cover' ? 'book-open' : 'bar-chart-2'}
                size={15}
                color={tok.accent}
              />
              <Text style={[type.label, copy.text, {color: tok.accent}]}>
                {backTo === 'cover' ? (
                  <Trans>Back to the book</Trans>
                ) : (
                  <Trans>Back to the report</Trans>
                )}
              </Text>
            </TouchableOpacity>
          )}
          <TouchableOpacity
            testID="flashbook-practice-done"
            accessibilityRole="button"
            onPress={onDone}
            style={[
              styles.actionBtn,
              {
                borderColor: pal.paperHairline,
                backgroundColor: pal.surfaceAlt,
                borderRadius: 14,
              },
            ]}>
            <Feather name="check" size={15} color={pal.muted} />
            <Text style={[type.label, copy.text, {color: pal.muted}]}>
              <Trans>Done</Trans>
            </Text>
          </TouchableOpacity>
        </View>
      </ScrollView>
    </View>
  )
}

/** One side of the comparison: a percentage, its fraction, and a bar. */
function ScoreSide({
  testID,
  label,
  pct,
  points,
  maxPoints,
  ink,
  bar,
}: {
  testID: string
  label: string
  pct: number
  points: number
  maxPoints: number
  ink: string
  bar: string
}) {
  const type = useReportType()
  const copy = useCopyStyle()
  const pal = useFlashBookPlayPalette()
  return (
    <View testID={testID} style={styles.scoreSide}>
      <Text style={[type.caption, copy.text, {color: pal.muted}]}>
        {label.toUpperCase()}
      </Text>
      <Text style={[type.display, copy.text, {color: ink}]}>{pct}%</Text>
      <Text style={[type.caption, copy.text, {color: pal.muted}]}>
        {points}/{maxPoints}
      </Text>
      <View style={[styles.scoreTrack, {backgroundColor: pal.paperHairline}]}>
        <View
          style={[styles.scoreFill, {width: `${pct}%`, backgroundColor: bar}]}
        />
      </View>
    </View>
  )
}

/** What the report said, what happened now, and the answer if it is still open. */
function QuestionRow({
  question,
  positive,
  negative,
  warning,
  accent,
}: {
  question: PracticeQuestion
  positive: string
  negative: string
  warning: string
  accent: string
}) {
  const {t: l} = useLingui()
  const type = useReportType()
  const copy = useCopyStyle()
  const pal = useFlashBookPlayPalette()
  const fixed = question.after?.verdict === 'right'
  const neverAsked = question.after === null

  return (
    <View
      testID={`flashbook-practice-row--${question.blockId}`}
      style={[
        styles.row,
        {borderColor: pal.paperHairline, backgroundColor: pal.surfaceAlt},
      ]}>
      <View style={styles.rowHead}>
        <Feather
          name={
            fixed ? 'check-circle' : neverAsked ? 'minus-circle' : 'x-circle'
          }
          size={15}
          color={fixed ? positive : neverAsked ? pal.muted : negative}
        />
        <Text style={[type.label, copy.text, {color: pal.text}, styles.flex]}>
          {question.prompt}
        </Text>
      </View>

      <View style={styles.marks}>
        <Mark
          label={l`Before`}
          verdict={question.before.verdict}
          positive={positive}
          negative={negative}
          warning={warning}
          muted={pal.muted}
        />
        <Feather
          name="arrow-right"
          size={12}
          color={pal.muted}
          style={styles.markArrow}
        />
        <Mark
          label={l`Now`}
          verdict={question.after?.verdict ?? 'skipped'}
          positive={positive}
          negative={negative}
          warning={warning}
          muted={pal.muted}
          unanswered={neverAsked}
        />
      </View>

      {/* Only on a question that is still not right: the answer is the thing worth
          having, and printing it on one that is now correct would spoil nothing but
          say nothing either. */}
      {!fixed && !!question.correctAnswer && (
        <Text style={[type.body, copy.text, {color: pal.body}]}>
          {l`Answer: ${question.correctAnswer}`}
        </Text>
      )}
      {!fixed && !!question.explanation && (
        <Text style={[type.caption, copy.text, {color: pal.muted}]}>
          {question.explanation}
        </Text>
      )}
      {fixed && <View style={[styles.fixedRule, {backgroundColor: accent}]} />}
    </View>
  )
}

function Mark({
  label,
  verdict,
  positive,
  negative,
  warning,
  muted,
  unanswered,
}: {
  label: string
  verdict: PracticeVerdict
  positive: string
  negative: string
  warning: string
  muted: string
  unanswered?: boolean
}) {
  const type = useReportType()
  const copy = useCopyStyle()
  const {t: l} = useLingui()
  const ink = unanswered
    ? muted
    : verdict === 'right'
      ? positive
      : verdict === 'partial'
        ? warning
        : negative
  const word = unanswered
    ? l`not asked`
    : verdict === 'right'
      ? l`right`
      : verdict === 'partial'
        ? l`part right`
        : verdict === 'skipped'
          ? l`skipped`
          : l`wrong`
  return (
    <Text style={[type.caption, copy.text, {color: ink}]}>
      {label}: {word}
    </Text>
  )
}

const styles = StyleSheet.create({
  root: {flex: 1},
  flex: {flex: 1},
  content: {paddingTop: 22, paddingBottom: 40},
  head: {gap: 6},
  scoreCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    borderWidth: 1,
    borderRadius: 14,
    paddingHorizontal: 14,
    paddingVertical: 14,
  },
  scoreSide: {flex: 1, gap: 2},
  scoreArrow: {alignItems: 'center', justifyContent: 'center'},
  scoreTrack: {height: 4, borderRadius: 2, marginTop: 6, overflow: 'hidden'},
  scoreFill: {height: 4, borderRadius: 2},
  section: {gap: 10},
  sectionTitle: {marginBottom: 2},
  row: {borderWidth: 1, borderRadius: 12, padding: 13, gap: 8},
  rowHead: {flexDirection: 'row', alignItems: 'center', gap: 8},
  marks: {flexDirection: 'row', alignItems: 'center', gap: 8},
  markArrow: {opacity: 0.8},
  fixedRule: {height: 3, width: 34, borderRadius: 2, marginTop: 2},
  actions: {gap: 8, marginTop: 4},
  actionBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    borderWidth: 1,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 14,
  },
})
